import { useRef, useState, type ChangeEvent } from 'react'
import { AtSign, CircleAlert, ImageUp, Save, Trash2 } from 'lucide-react'
import { Avatar, Button, ConfirmDialog, Form, IconButton, TextField, useToast } from '@/components/play'
import { displayNameFor, normalizeUsername, personToneKey, usernameProblem } from '@/lib/profile'
import { prepareProfilePhoto, UnreadablePhotoError } from '@/lib/profilePhoto'
import { useAuth } from '@/state/AuthContext'
import type { UserResource } from '@/types/users'
import { api } from '@/util/api'
import { fieldErrorsFrom } from './accountErrors'

const PROFILE_FIELDS = ['first_name', 'last_name', 'username'] as const

/** Photo, name and username for the signed-in person. A chosen photo uploads straight away; names save together. */
export function ProfileSection({ user }: { user: UserResource }) {
  const { updateUser } = useAuth()
  const { toast } = useToast()
  const attributes = user.attributes
  const [firstName, setFirstName] = useState(attributes.first_name ?? '')
  const [lastName, setLastName] = useState(attributes.last_name ?? '')
  const [username, setUsername] = useState(attributes.username ?? '')
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [saving, setSaving] = useState(false)

  const photoInputRef = useRef<HTMLInputElement>(null)
  const [photoAction, setPhotoAction] = useState<'upload' | 'remove' | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const avatarUrl = attributes.avatar_url
  // Each request returns the whole user, so a photo change and a profile save run one at a time: otherwise the
  // later response could replace the signed-in user with the other request's stale fields.
  const busy = saving || photoAction !== null

  async function save() {
    setSaving(true)
    setErrors({})
    try {
      const res = await api.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        username: normalizeUsername(username),
      })
      updateUser(res.data)
      setUsername(res.data.attributes.username ?? '')
      toast({ title: 'Profile saved', tone: 'success' })
    } catch (failure) {
      const fieldErrors = fieldErrorsFrom(failure, PROFILE_FIELDS)
      if (fieldErrors) setErrors(fieldErrors)
      else toast({ title: 'Profile not saved', description: 'Check your connection and try again.', tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function uploadPhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Clear the input so choosing the same file again still uploads it.
    event.target.value = ''
    if (!file) return
    setPhotoError(null)
    setPhotoAction('upload')
    try {
      const photo = await prepareProfilePhoto(file)
      const res = await api.uploadAvatar(photo, 'profile-photo.jpg')
      updateUser(res.data)
      toast({ title: 'Photo updated', tone: 'success' })
    } catch (failure) {
      setPhotoError(
        failure instanceof UnreadablePhotoError
          ? 'That file couldn’t be opened as a photo. Try a JPEG or PNG.'
          : fieldErrorsFrom(failure, ['avatar'])?.avatar[0] ?? 'Your photo didn’t upload. Try again.',
      )
    } finally {
      setPhotoAction(null)
    }
  }

  // Runs from the removal confirmation, which shows progress until this settles and keeps a failure in the dialog.
  async function removePhoto() {
    setPhotoError(null)
    setPhotoAction('remove')
    try {
      const res = await api.removeAvatar()
      updateUser(res.data)
      toast({ title: 'Photo removed', tone: 'success' })
    } finally {
      setPhotoAction(null)
    }
  }

  return (
    <section className="ps-section" aria-labelledby="profile-heading">
      <h2 id="profile-heading">Profile</h2>
      <div className="ps-fields">
        <div className="ps-field">
          <div className="ps-field-label"><span id="profile-photo-label">Photo</span></div>
          <div className="pa-photo-row">
            <Avatar
              size="large"
              name={displayNameFor(user)}
              src={avatarUrl}
              tone="auto"
              toneKey={personToneKey({ handle: attributes.username, id: user.id })}
            />
            <Button
              variant="secondary"
              loading={photoAction === 'upload'}
              disabled={busy}
              aria-describedby={photoError ? 'profile-photo-error' : undefined}
              onClick={() => photoInputRef.current?.click()}
            >
              <ImageUp aria-hidden="true" />
              {avatarUrl ? 'Change photo' : 'Upload photo'}
            </Button>
            {avatarUrl && (
              <ConfirmDialog
                trigger={
                  <IconButton label="Remove photo" variant="ghost" disabled={busy}>
                    <Trash2 />
                  </IconButton>
                }
                title="Remove your photo?"
                description="Your initials show in its place. You can upload a photo again at any time."
                confirmLabel="Remove photo"
                onConfirm={removePhoto}
                failureMessage="Your photo wasn’t removed. Try again."
              />
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              tabIndex={-1}
              aria-labelledby="profile-photo-label"
              onChange={uploadPhoto}
            />
          </div>
          {photoError && (
            <p id="profile-photo-error" role="alert" className="play-form-field__error">
              <CircleAlert aria-hidden="true" />
              <span>{photoError}</span>
            </p>
          )}
        </div>

        <Form className="ps-fields" errors={errors} onFormSubmit={save}>
          <div className="pa-name-row">
            <TextField
              name="first_name"
              label="First name"
              autoComplete="given-name"
              maxLength={80}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <TextField
              name="last_name"
              label="Last name"
              autoComplete="family-name"
              maxLength={80}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
          <TextField
            name="username"
            label="Username"
            description="3 to 30 lowercase letters, numbers or underscores."
            icon={<AtSign />}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={31}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            validate={(value) => usernameProblem(String(value ?? ''))}
          />
          <div>
            <Button type="submit" loading={saving} disabled={photoAction !== null}>
              <Save aria-hidden="true" />
              Save profile
            </Button>
          </div>
        </Form>
      </div>
    </section>
  )
}
